import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ScanSearch,
  Flag,
  Layers,
  Activity,
  ArrowUpRight,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";
import { getDashboard, formatCaseId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ScoreBadge from "@/components/ScoreBadge";
import StatusBadge from "@/components/StatusBadge";
import { CardSkeleton, SkeletonBlock } from "@/components/Skeletons";

const STAT_DEFS = [
  {
    key: "analysesToday",
    label: "Analyses today",
    icon: Activity,
    hint: "Across all batches",
  },
  {
    key: "flaggedImages",
    label: "Flagged images",
    icon: Flag,
    hint: "Awaiting editor review",
    accent: "text-red-400",
  },
  {
    key: "batchesInProgress",
    label: "Batches in progress",
    icon: Layers,
    hint: "Live processing queue",
    accent: "text-sky-400",
  },
];

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    setErr(null);
    getDashboard()
      .then((d) => active && setData(d))
      .catch((e) => active && setErr(e.message || "Failed to load dashboard"));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
            Welcome back
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            {user?.name || "Picture editor"}'s desk
          </h1>
          <p className="text-sm text-zinc-400 mt-2">
            A high-level view of today's verification workload.
          </p>
        </div>
        <button
          onClick={() => navigate("/analyze")}
          data-testid="dashboard-new-analysis-button"
          className="inline-flex items-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 transition-colors"
        >
          <ScanSearch className="h-4 w-4" />
          Start new analysis
        </button>
      </header>

      {err && (
        <div
          role="alert"
          data-testid="dashboard-error"
          className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3"
        >
          {err}
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dashboard-stats">
        {!data
          ? STAT_DEFS.map((s) => <CardSkeleton key={s.key} />)
          : STAT_DEFS.map(({ key, label, icon: Icon, hint, accent }) => (
              <div
                key={key}
                data-testid={`stat-${key}`}
                className="rounded-xl border border-[#272A30] bg-[#111318] p-5 hover:border-[#4A4E58] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    {label}
                  </div>
                  <Icon className={`h-4 w-4 ${accent || "text-zinc-500"}`} />
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-4xl font-semibold tracking-tight tabular-nums">
                    {data.stats[key]}
                  </span>
                </div>
                <div className="mt-1 text-xs text-zinc-500">{hint}</div>
              </div>
            ))}
      </section>

      <section className="rounded-xl border border-[#272A30] bg-[#111318]" data-testid="recent-cases">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#272A30]">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Recent cases</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Latest verifications. Click a row to open the report.
            </p>
          </div>
          <Link
            to="/history"
            data-testid="view-all-cases-link"
            className="text-xs uppercase tracking-[0.18em] text-zinc-400 hover:text-white inline-flex items-center gap-1"
          >
            View all
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {!data ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonBlock key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : data.recent.length === 0 ? (
          <div className="p-10 text-center" data-testid="recent-cases-empty">
            <div className="text-zinc-400">No cases yet.</div>
            <button
              onClick={() => navigate("/analyze")}
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-1.5 text-xs uppercase tracking-wider text-zinc-300 hover:bg-[#1A1D24]"
            >
              Submit your first image
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-5 py-3 text-left font-medium">Case</th>
                <th className="px-5 py-3 text-left font-medium">Submitted</th>
                <th className="px-5 py-3 text-left font-medium">Source</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium">Score</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.recent.map((c) => {
                const displayId = formatCaseId(c.id);
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/report/${c.id}`)}
                    data-testid={`recent-case-row-${c.id}`}
                    className="border-t border-[#272A30]/60 hover:bg-[#14171D] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {c.thumbnail ? (
                          <img
                            src={c.thumbnail}
                            alt=""
                            className="h-10 w-14 rounded-md object-cover ring-1 ring-[#272A30]"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-10 w-14 rounded-md bg-[#1A1D24] ring-1 ring-[#272A30] flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="h-4 w-4 text-zinc-600" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-zinc-100">{c.title}</div>
                          <div className="text-[11px] text-zinc-500 mono">{displayId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-zinc-400 whitespace-nowrap">
                      {formatTime(c.submittedAt)}
                    </td>
                    <td className="px-5 py-3 text-sm text-zinc-400 mono">{c.sourceDomain || "—"}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-5 py-3">
                      <ScoreBadge score={c.score} />
                    </td>
                    <td className="px-2 py-3 text-zinc-500">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
